import { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

const CARD_IMAGES = [
  {
    id: "fluffy",
    name: "Fluffy",
    image: "https://customer-assets.emergentagent.com/job_3476f179-e28e-4a73-ad04-dc3e86ca447d/artifacts/jhuj9aso_ldsartbot_httpss.mj.runWowi96QL37o_the_poodle_dad_of_this_image_6f6633ef-37c6-490d-9aa1-8e71717fe520%20%281%29.png",
  },
  {
    id: "cotton",
    name: "Cotton",
    image: "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/ae01nwov_ldsartbot_httpss.mj.runWowi96QL37o_Create_a_cute_pastel-coloure_8933b4a2-3c9f-4656-aa3a-d936a9b83a93.png",
  },
  {
    id: "mochi",
    name: "Mochi",
    image: "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/4n0ynx1b_ldsartbot_httpss.mj.runWowi96QL37o_the_poodle_dad_of_this_image_72e5033a-93f6-48b5-b289-275a8954aee6.png",
  },
];

const GRID_SIZES = [
  { label: "Easy", pairs: 3, cols: 3 },
  { label: "Medium", pairs: 6, cols: 4 },
  { label: "Hard", pairs: 9, cols: 6 },
  { label: "Expert", pairs: 12, cols: 6 },
];

const MemoryMatchGame = ({ isOpen, onClose }) => {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameState, setGameState] = useState('ready');
  const [difficulty, setDifficulty] = useState(0);
  const [time, setTime] = useState(0);
  const timerRef = useRef(null);

  const createDeck = (pairCount) => {
    const deck = [];
    for (let i = 0; i < pairCount; i++) {
      const cardData = CARD_IMAGES[i % CARD_IMAGES.length];
      deck.push({ ...cardData, uid: `${i}-a` });
      deck.push({ ...cardData, uid: `${i}-b` });
    }
    // Shuffle
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  };

  const startGame = (diffIndex) => {
    setDifficulty(diffIndex);
    const grid = GRID_SIZES[diffIndex];
    setCards(createDeck(grid.pairs));
    setFlipped([]);
    setMatched([]);
    setMoves(0);
    setTime(0);
    setGameState('playing');
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setTime(t => t + 1), 1000);
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    if (matched.length > 0 && matched.length === cards.length) {
      setGameState('won');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, [matched, cards.length]);

  const handleCardClick = (index) => {
    if (gameState !== 'playing') return;
    if (flipped.length >= 2) return;
    if (flipped.includes(index)) return;
    if (matched.includes(index)) return;

    const newFlipped = [...flipped, index];
    setFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      if (cards[first].id === cards[second].id) {
        setTimeout(() => {
          setMatched(prev => [...prev, first, second]);
          setFlipped([]);
        }, 500);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  const formatTime = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getMessage = () => {
    const grid = GRID_SIZES[difficulty];
    const perfectMoves = grid.pairs;
    if (moves <= perfectMoves + 2) return "Perfect memory! Amazing! 🌟";
    if (moves <= perfectMoves * 2) return "Brilliant! Great memory! ✨";
    if (moves <= perfectMoves * 3) return "Well done! You found them all! 💛";
    return "You did it! Great job! 🎉";
  };

  if (!isOpen) return null;

  const grid = GRID_SIZES[difficulty];

  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4 py-6">
        <div className="bg-[#FDFBF7] rounded-3xl p-4 sm:p-6 max-w-lg w-full text-center shadow-2xl relative" data-testid="memory-match-game">
          <button onClick={() => { onClose(); if (timerRef.current) clearInterval(timerRef.current); }} 
            className="absolute top-4 right-4 text-[#9A9A9A] hover:text-[#4A4A4A] transition-colors z-10" data-testid="memory-close">
            <X className="w-6 h-6" />
          </button>

          <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-3">Poodle Match</h2>

          {/* Ready Screen */}
          {gameState === 'ready' && (
            <div className="py-6">
              <p className="text-4xl mb-4">🐩</p>
              <p className="text-sm text-[#6B6B6B] mb-6">Find all the matching poodle pairs!</p>
              <div className="flex flex-col gap-3 max-w-xs mx-auto">
                {GRID_SIZES.map((g, i) => (
                  <button key={i} onClick={() => startGame(i)}
                    data-testid={`memory-start-${g.label.toLowerCase()}`}
                    className="px-6 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
                    {g.label} ({g.pairs} pairs)
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Playing */}
          {(gameState === 'playing' || gameState === 'won') && (
            <>
              <div className="flex justify-center gap-6 mb-3 text-sm">
                <div className="text-center">
                  <p className="text-[#9A9A9A] text-xs">Moves</p>
                  <p className="font-bold text-[#4A4A4A]">{moves}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#9A9A9A] text-xs">Time</p>
                  <p className="font-bold text-[#4A4A4A]">{formatTime(time)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[#9A9A9A] text-xs">Pairs</p>
                  <p className="font-bold text-[#4A4A4A]">{matched.length / 2}/{grid.pairs}</p>
                </div>
              </div>

              <div
                className="grid gap-2 mx-auto mb-4"
                style={{
                  gridTemplateColumns: `repeat(${grid.cols}, 1fr)`,
                  maxWidth: grid.cols <= 3 ? '280px' : grid.cols <= 4 ? '340px' : '420px',
                }}
              >
                {cards.map((card, index) => {
                  const isFlipped = flipped.includes(index) || matched.includes(index);
                  const isMatched = matched.includes(index);
                  return (
                    <button
                      key={card.uid}
                      onClick={() => handleCardClick(index)}
                      data-testid={`memory-card-${index}`}
                      className={`aspect-square rounded-xl overflow-hidden transition-all duration-300 ${
                        isMatched ? 'ring-2 ring-[#5A8A5A] scale-95 opacity-80' :
                        isFlipped ? 'ring-2 ring-[#B89823]' :
                        'bg-[#E8D4A8] hover:bg-[#D9C599] hover:scale-105 cursor-pointer'
                      }`}
                    >
                      {isFlipped ? (
                        <img src={card.image} alt={card.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-2xl sm:text-3xl">🐩</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Won Overlay */}
              {gameState === 'won' && (
                <div className="bg-[#F9F6EE] rounded-2xl p-5 border border-[#E8E5DC]">
                  <p className="text-3xl mb-2">🎉</p>
                  <p className="text-lg font-bold text-[#4A4A4A] mb-1">You found them all!</p>
                  <p className="text-sm text-[#6B6B6B] mb-1">{moves} moves in {formatTime(time)}</p>
                  <p className="text-sm font-medium text-[#B89823] mb-4">{getMessage()}</p>
                  <div className="flex gap-3 justify-center">
                    <button onClick={() => startGame(difficulty)} data-testid="memory-play-again"
                      className="px-5 py-2.5 bg-[#E8D4A8] text-[#5A4A32] rounded-full text-sm font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
                      Play Again
                    </button>
                    <button onClick={() => setGameState('ready')}
                      className="px-5 py-2.5 bg-[#F3F0E6] text-[#6B6B6B] rounded-full text-sm font-semibold hover:bg-[#E8E5DC] transition-all">
                      Change Difficulty
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemoryMatchGame;
